import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../components/admin/AdminLayout";
import { ArrowLeft, Eye, Prohibit, CheckCircle } from "@phosphor-icons/react";

export default function AdminOwnerDetail() {
  const { ownerId } = useParams();
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOwner = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/owners/${ownerId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) throw new Error("Owner not found");
      setOwner(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { fetchOwner(); }, [fetchOwner]);

  async function toggleStatus() {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/owners/${ownerId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("rs_token")}`,
        },
        body: JSON.stringify({ is_disabled: !owner.is_disabled }),
      });
      if (!res.ok) throw new Error("Failed to update");
      toast.success(`Account ${owner.is_disabled ? "enabled" : "disabled"}`);
      fetchOwner();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64"><div className="tag">Loading...</div></div>
      </AdminLayout>
    );
  }

  if (!owner) {
    return (
      <AdminLayout>
        <div className="text-center py-20 text-[var(--ink-muted)]">Owner not found</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Link to="/admin/owners" className="p-2 rounded-lg hover:bg-gray-100">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="font-black text-xl">{owner.name}</h2>
            <p className="text-sm text-[var(--ink-muted)]">{owner.email} · {owner.business_name}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={toggleStatus}
              disabled={saving}
              className={`btn-pop text-sm flex items-center gap-1.5 ${owner.is_disabled ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
            >
              {owner.is_disabled ? <><CheckCircle size={14} /> Enable</> : <><Prohibit size={14} /> Disable</>}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="card-pop p-4">
            <div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Outlets</div>
            <div className="text-2xl font-black mt-1">{owner.outlets?.length || 0}</div>
          </div>
          <div className="card-pop p-4">
            <div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Total Scans</div>
            <div className="text-2xl font-black mt-1">{owner.total_scans || 0}</div>
          </div>
          <div className="card-pop p-4">
            <div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Total Spins</div>
            <div className="text-2xl font-black mt-1">{owner.total_spins || 0}</div>
          </div>
        </div>

        <div className="card-pop p-4">
          <h3 className="font-bold mb-3">Owned Outlets</h3>
          {owner.outlets?.length === 0 ? (
            <p className="text-sm text-[var(--ink-muted)] py-4 text-center">No outlets</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: "var(--border)" }}>
                    <th className="py-2 px-3 text-left font-bold">Name</th>
                    <th className="py-2 px-3 text-left font-bold">Slug</th>
                    <th className="py-2 px-3 text-right font-bold">Scans</th>
                    <th className="py-2 px-3 text-right font-bold">Spins</th>
                    <th className="py-2 px-3 text-center font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {owner.outlets.map((o) => (
                    <tr key={o.id} className="border-b hover:bg-gray-50" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 px-3 font-medium">{o.name}</td>
                      <td className="py-2 px-3 text-[var(--ink-muted)]">{o.slug}</td>
                      <td className="py-2 px-3 text-right">{o.scans_count ?? "-"}</td>
                      <td className="py-2 px-3 text-right">{o.spins_count ?? "-"}</td>
                      <td className="py-2 px-3 text-center">
                        <Link to={`/admin/outlets/${o.id}`} className="p-1.5 rounded hover:bg-orange-100 text-orange-600 inline-block">
                          <Eye size={16} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}